import * as _ from 'underscore';
import { setOrderAction } from '../app/actions/SetOrderAction';
import { appStore } from '../app/AppStore';
import { selectContentSort } from '../app/selectors/ContentSortSelectors';
import { UrlParamKeys } from '../app/UrlParamKeys';
import { Order } from '../ts/helpers/Order';
import { IOrderClause } from '../ts/ServerApi/types/IOrderClause';
import { WindowLocationService } from '../commonInfra/services/WindowLocationService';

export enum SubscriptionBehavior {
    SubscribeToReduxStateChange,
    None
}

/**
 * Manages the state of the 'order' parameter in the url for the datagrid. Implements primary, secondary, etc. ordering.
 */
export class Sorter {

  private order: ReadonlyArray<IOrderClause> = selectContentSort(appStore.getState());
  private unsubscribe: Function = _.noop;

  constructor(private defaultOrder: ReadonlyArray<IOrderClause>, subscriptionBehavior: SubscriptionBehavior = SubscriptionBehavior.SubscribeToReduxStateChange) {
    if (subscriptionBehavior === SubscriptionBehavior.SubscribeToReduxStateChange) {
      this.unsubscribe = appStore.subscribe(this.updateOrder.bind(this));
    }
  }

  public detach() {
    this.unsubscribe();
  }

  public setPrimary(field: string, direction: string): void {
    if (!field) { return; }

    const newOrder = this.orderWithClausePrepended(this.order, field, direction);

    this.setOrder(newOrder);
  }

  public removeFieldAndSetPrimary(field: string, newField: string, newDirection: string): void {
    if (!field || !newField) { return; }

    let newOrder = Order.withoutField(this.order, field);
    newOrder = this.orderWithClausePrepended(newOrder, newField, newDirection);

    this.setOrder(newOrder);
  }

  public setOrder(order: ReadonlyArray<IOrderClause>): void {
    appStore.dispatch(setOrderAction(order));
  }

  public isSortAscending(): boolean {
    return this.order[0] && this.order[0].ascending;
  }

  public selectedColumnKey(): string {
    return this.order.length > 0 ? this.order[0].field : '';
  }

  public isSortAscendingBy(key: string) {
    return this.selectedColumnKey() === key && this.isSortAscending();
  }

  public isSortDescendingBy(key: string) {
    return this.selectedColumnKey() === key && !this.isSortAscending();
  }

  public updateLocationSearch(order: ReadonlyArray<IOrderClause>) {
    const value = _.isEqual(order, this.defaultOrder) ? null : Order.serialize(order);
    WindowLocationService.setHashQueryParameter(UrlParamKeys.Order, value);
  }

  private orderWithClausePrepended(order: ReadonlyArray<IOrderClause>, field: string, direction: string): ReadonlyArray<IOrderClause> {
    if (Order.startsWithField(order, field)) {
      if (_.isEqual(order, this.defaultOrder) && order.length > 0) {
        // keep the first clause of the default order
        order = order.slice(0, 1);
      }
      order = Order.withFirstClauseToggled(order);
    } else {
      if (_.isEqual(order, this.defaultOrder)) {
        // clear the order so that default order clauses don't get inserted into the URL
        order = [];
      }
      order = Order.withClausePrepended(order, { field: field, ascending: direction === 'asc' });
    }
    return order;
  }

  private updateOrder() {
    const oldOrder = this.order;
    const newOrder = this.order = selectContentSort(appStore.getState());
    if (!_.isEqual(oldOrder, newOrder) && newOrder && newOrder.length > 0) {
      this.updateLocationSearch(newOrder);
    }
  }
}